import { Grid, List } from 'semantic-ui-react'
import { Text, Card, GridRowColumn } from '@/base'
import { BalanceSheetReportData } from '@/actions/reportActions'
import { formatCurrencyFromCentsAndRound } from '@/utils/currencyHelpers'
import { BalanceSheetCategory } from './BalanceSheetCategory'

interface BalanceSheetReportProps {
  balanceSheetDate: string
  reportData: BalanceSheetReportData
  isPdf?: boolean
}

const BalanceSheetReport = ({
  balanceSheetDate,
  reportData,
  isPdf = false,
}: BalanceSheetReportProps) => {
  const hasOpeningBalanceEquity = reportData.accounts.Equity.subcategories.some(
    (sub) =>
      sub.accounts.some((acc) =>
        /^opening\s+balance\s+equity\s*\*?\s*$/i.test(acc.label?.trim() ?? '')
      )
  )

  return (
    <Grid style={{ width: '90%', margin: '0 auto' }} columns={'equal'}>
      {/* Header */}
      <GridRowColumn short>
        <Text as="h2" textAlign="center">
          Balance Sheet
        </Text>
      </GridRowColumn>
      <GridRowColumn short>
        <Text textAlign="center">As of {balanceSheetDate}</Text>
      </GridRowColumn>
      <GridRowColumn />

      {/* Table / Report */}
      <Grid.Row>
        <Grid.Column>
          <Text as="h2">Assets</Text>
        </Grid.Column>
        <Grid.Column>
          <Text as="h2">Liabilities</Text>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column>
          <BalanceSheetCategory
            categoryData={reportData.accounts.Assets}
            totalLabel="Total Assets"
            isPdf={isPdf}
            balanceSheetDate={balanceSheetDate}
          />
        </Grid.Column>
        <Grid.Column>
          <BalanceSheetCategory
            categoryData={reportData.accounts.Liability}
            totalLabel="Total Liabilities"
            isPdf={isPdf}
            balanceSheetDate={balanceSheetDate}
          />
          <Text as="h2" style={{ marginTop: 48, marginBottom: 24 }}>
            Equity
          </Text>
          <BalanceSheetCategory
            categoryData={reportData.accounts.Equity}
            totalLabel="Total Equity"
            isPdf={isPdf}
            balanceSheetDate={balanceSheetDate}
          />
        </Grid.Column>
      </Grid.Row>
      <GridRowColumn />

      <GridRowColumn>
        <Card type="subsection" fullWidth>
          <Grid>
            <Grid.Row>
              <Grid.Column width={7}>
                <List>
                  <List.Item>
                    <List.Content floated="right">
                      <Text as="h3">
                        {formatCurrencyFromCentsAndRound(
                          reportData.totals.totalAssetsInCents,
                          { hideDecimalsIfZero: true }
                        )}
                      </Text>
                    </List.Content>
                    <List.Content>
                      <Text as="h3">Total Assets</Text>
                    </List.Content>
                  </List.Item>
                </List>
              </Grid.Column>
              <Grid.Column width={2}>
                <Text as="h3" textAlign="center">
                  =
                </Text>
              </Grid.Column>
              <Grid.Column width={7}>
                <List>
                  <List.Item>
                    <List.Content floated="right">
                      <Text as="h3">
                        {formatCurrencyFromCentsAndRound(
                          reportData.totals.totalLiabilitiesEquityInCents,
                          { hideDecimalsIfZero: true }
                        )}
                      </Text>
                    </List.Content>
                    <List.Content floated="left">
                      <Text as="h3">Total Liabilities & Equities</Text>
                    </List.Content>
                  </List.Item>
                </List>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Card>
      </GridRowColumn>

      {/* Opening Balance Equity Disclaimer — only shown when the account exists */}
      {hasOpeningBalanceEquity && (
        <GridRowColumn>
          <Text
            as="bodySm"
            style={{ color: '#666', fontStyle: 'italic', marginTop: 8 }}
          >
            * The Opening Balance Equity shown on this Balance Sheet is an
            assumed balancing figure used to balance assets = liability +
            equity. This amount is not based on verified records, is not
            reconciled to Retained Earnings, and should not be used as an
            accurate representation of the business&apos;s financial position.
          </Text>
        </GridRowColumn>
      )}
    </Grid>
  )
}

export default BalanceSheetReport
